import React, { useCallback, useEffect, useState } from 'react';
import { FileCheck2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { api } from '../lib/api';
import { usePilotContext } from '../hooks/usePilotContext';
import { toast } from 'sonner';

export default function CompliancePage() {
  const { context } = usePilotContext();
  const [items, setItems] = useState([]);
  const [framework, setFramework] = useState('SOC2');
  const [tagIds, setTagIds] = useState(() => new Set(['IEC62443']));
  const [catalog, setCatalog] = useState([]);
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [reportId, setReportId] = useState('');
  const [signerName, setSignerName] = useState('');
  const [signerRole, setSignerRole] = useState('Independent Auditor');
  const [attestText, setAttestText] = useState(
    'I attest that the controls described in this report were evaluated for the stated period.',
  );

  const load = useCallback(async () => {
    if (!context.customer_id) return;
    try {
      const data = await api.get(`/api/compliance/${context.customer_id}/reports`);
      setItems(data.items || []);
    } catch (e) {
      console.error(e);
    }
  }, [context.customer_id]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleTag = (id) => {
    setTagIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const createReport = async () => {
    try {
      const res = await api.post(`/api/compliance/${context.customer_id}/reports`, {
        framework,
        framework_tags: Array.from(tagIds),
        period_start: periodStart || undefined,
        period_end: periodEnd || undefined,
        summary: { source: 'pilot-ui' },
      });
      toast.success(`Report #${res.id} created`);
      setReportId(String(res.id));
      await load();
    } catch (e) {
      toast.error(e.message || 'Create failed');
    }
  };

  const attest = async () => {
    if (!reportId) {
      toast.error('Set report ID');
      return;
    }
    try {
      const res = await api.post(
        `/api/compliance/${context.customer_id}/reports/${reportId}/attest`,
        {
          signer_name: signerName,
          signer_role: signerRole,
          attestation_text: attestText,
        },
      );
      toast.success(`Attested — hash ${res.signature_hash?.slice(0, 16)}…`);
      await load();
    } catch (e) {
      toast.error(e.message || 'Attestation failed (auditor/admin only)');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-bold text-foreground">Compliance</h1>
        <p className="text-muted-foreground">
          Draft compliance reports and record auditor attestations (signature hash stored server-side).
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileCheck2 className="h-5 w-5 text-primary" />
            New report
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-2">
          <Input value={framework} onChange={(e) => setFramework(e.target.value)} placeholder="Framework (e.g. SOC2)" />
          <Input value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} placeholder="Period start" />
          <Input value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} placeholder="Period end" />
          <Button className="md:col-span-2" type="button" variant="secondary" onClick={createReport}>
            Create draft report
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Attestation</CardTitle>
          <CardDescription>Requires JWT role admin or auditor.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Input value={reportId} onChange={(e) => setReportId(e.target.value)} placeholder="Report ID" />
          <Input value={signerName} onChange={(e) => setSignerName(e.target.value)} placeholder="Signer name" />
          <Input value={signerRole} onChange={(e) => setSignerRole(e.target.value)} placeholder="Signer role" />
          <textarea
            className="min-h-[120px] w-full rounded-md border border-border bg-secondary/30 p-3 text-sm"
            value={attestText}
            onChange={(e) => setAttestText(e.target.value)}
          />
          <Button type="button" onClick={attest}>
            Record attestation
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Reports</CardTitle>
        </CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground">No reports.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {items.map((r) => (
                <li key={r.id} className="border-b border-border py-2">
                  <span className="font-mono">#{r.id}</span> {r.framework} — {r.status}
                  {r.framework_tags?.length ? (
                    <span className="ml-2 text-xs text-muted-foreground">
                      [{r.framework_tags.join(', ')}]
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
